import { DateFormats, DateUtil } from "utilities/date-utilities";
import RoomTypeView from "../roomType/RoomTypeView";
import * as lodash from "lodash";
import If from "components/common/If";

const colors = {
    blue: 'bg-blue-50 dark:bg-blue-950',
    red: 'bg-red-50 dark:bg-red-950',
    green: 'bg-green-50 dark:bg-green-950',
    yellow: 'bg-yellow-50 dark:bg-yellow-950',
    orange: 'bg-orange-50 dark:bg-orange-950',
    purple: 'bg-purple-50 dark:bg-purple-950',
    pink: 'bg-pink-50 dark:bg-pink-950',
    white: ''
}

export type AvailabilityRoomType = {
    id: number;
    name: string;
    order?: number | null | undefined;
    roomType: {
        id: number;
        color?: string | null | undefined;
        name: string;
        defaultPrice?: number | null | undefined;
    },
    bookingDetails: AvailabilityBookingDetail[];
};
type AvailabilityBookingDetail = {
    price: number,
    booking: {
        id: number;
        inDate: Date;
        inSeasonId: number;
        outDate: Date;
        roomType: {
            id: number;
            name: string;
            color?: string | null | undefined;
        }
    },
    isSelected?: boolean;
    date: Date;
    id: number;
    roomId: number;
    changed?: boolean;
};

export type SelectData = {
    room: AvailabilityRoomType;
    bookingDetail: AvailabilityBookingDetail;
}

type AvailabilityTablePropsType = {
    dates: Date[];
    data: AvailabilityRoomType[],
    selectedDates?: Date[];
    selectedRoomTypeId?: number;
    onChange: (roomId: number, date: Date) => void;
    onSelect: (e: React.MouseEvent, data?: SelectData) => void;
    view: (e: React.MouseEvent, data: SelectData) => void;
}

type AvailabilityTableRowPropsType = Omit<AvailabilityTablePropsType, 'onChange' | 'onSelect'> & {
    room: AvailabilityRoomType;
    onClick: (e: React.MouseEvent, SelectData?: SelectData) => void;
    onDoubleClick: (e: React.MouseEvent, SelectData: SelectData) => void;
}

type AvailabilityTableColumnPropsType = AvailabilityTableRowPropsType & {
    bookingDetails?: AvailabilityBookingDetail[];
    className?: string;
    date: Date;
}

type AvailabilityTableColumnContentPropsType = AvailabilityTableRowPropsType & {
    bookingDetail: AvailabilityBookingDetail;
    conflict: boolean;
}

const AvailabilityTableColumnContent = (props: AvailabilityTableColumnContentPropsType) => {
    const { room } = props;
    const { bookingDetail } = props;
    const conflict = room.bookingDetails.filter(d => DateUtil.compareOnAsIsoDate(new Date(d.date), new Date(bookingDetail.date))).length > 1;
    let timer: any = null;
    const tagData = {
        "data-room-id": room.id,
        "data-date": DateUtil.format(bookingDetail.date, DateFormats.ISO),
        "data-booking-detail-id": bookingDetail.id,
        "data-booking-id": bookingDetail.booking.id,
    }

    const onClick = (e: React.MouseEvent, SelectData: SelectData) => {
        clearTimeout(timer);
        if (e.detail === 1) {
            timer = setTimeout(() => {
                props.onClick(e, SelectData);
            }, 200);
        }
        else if (e.detail === 2) {
            props.onDoubleClick(e, SelectData);
        }
    }

    return (
        <div className='relative w-min mx-auto' {...tagData}>
            <button
                className={`booking-item ${!bookingDetail.isSelected && conflict ? 'conflict' : ''} ${bookingDetail.changed ? 'changed' : ''} ${bookingDetail.isSelected ? 'selected' : ''}`.trim()}
                type="button"
                id={`${room.id}-${DateUtil.toIsoDate(bookingDetail.date)}-${bookingDetail.booking.id}`}
            >
                <span>{bookingDetail.booking.inSeasonId}</span>
                <If condition={bookingDetail.booking.roomType.id !== room.roomType.id}>
                    <span> ({bookingDetail.booking.roomType.name[0].toUpperCase()})</span>
                </If>
            </button>
            <div
                {...tagData}
                className="booking-wrapper"
                draggable
                onDragStart={e => props.onClick(e, { room, bookingDetail })}
                onDragEnd={e => props.onClick(e, undefined)}
                onClick={e => onClick(e, { room, bookingDetail })}
            />
        </div>
    )
}

const AvailabilityTableColumn = (props: AvailabilityTableColumnPropsType) => {
    const { room, bookingDetails, date } = props;

    let isSelectedDate = isSelected(props.selectedRoomTypeId, room.roomType.id, date, props.selectedDates);
    isSelectedDate += ` ${(colors as any)[room.roomType.color!]}`

    const tdData = {
        "data-room-id": room.id,
        "data-date": DateUtil.format(date, DateFormats.ISO),
    }

    if (!bookingDetails || !bookingDetails.length) {
        return <td {...tdData} onClick={props.onClick} className={isSelectedDate}></td>;
    }
    return (
        <td className={isSelectedDate} {...tdData}>
            {bookingDetails.map(bookingDetail => {
                return (
                    <AvailabilityTableColumnContent
                        {...props}
                        key={`${room.id}-${DateUtil.toIsoDate(bookingDetail.date)}-${bookingDetail.booking.id}`}
                        bookingDetail={bookingDetail}
                        conflict={bookingDetails.length > 1}
                    />
                );
            })}
        </td>
    )
}

const AvailabilityTableRow = (props: AvailabilityTableRowPropsType) => {
    const { room } = props;

    return (
        <>
            <td className="sticky-col">
                <RoomTypeView color={room.roomType.color} name={`${room.name} - ${room.roomType.name[0]}`} />
            </td>
            {props.dates.map(date => {
                const bookingDetails = room.bookingDetails.filter(b => DateUtil.compareOnAsIsoDate(b.date, date));
                return <AvailabilityTableColumn
                    {...props}
                    date={date}
                    onClick={props.onClick}
                    key={`${room.id}-${DateUtil.toIsoDate(date)}`}
                    bookingDetails={bookingDetails}
                />
            })}
        </>
    )
}

const AvailabilityTable = (props: AvailabilityTablePropsType) => {
    const onDragEnter = (event: React.DragEvent) => {
        const target = event.target as any;
        if (target.tagName === 'TD') {
            event.preventDefault();
            props.onChange(Number(target.dataset.roomId), target.dataset.date);
        }
    }

    return (
        <table className="w-full">
            <thead>
                <tr>
                    <th className="sticky-col">Room</th>
                    {props.dates.map(date => <th key={DateUtil.toIsoDate(date)}>{DateUtil.format(date, DateFormats.SHORT)}</th>)}
                </tr>
            </thead>
            <tbody>
                {props.data.map(room => {
                    return (
                        <tr
                            key={room.id}
                            onDragEnter={onDragEnter}
                        >
                            <AvailabilityTableRow
                                {...props}
                                room={room}
                                onClick={props.onSelect}
                                onDoubleClick={props.view}
                            />
                        </tr>
                    );
                })}
                {
                    lodash.unionBy(props.data, x => x.roomType.id).map(roomTypeId => {
                        const roomType = props.data.find(x => x.roomType.id === roomTypeId.roomType.id)!;
                        const roomCount = props.data.filter(x => x.roomType.id === roomTypeId.roomType.id).length;
                        const available: { date: Date, count: number, roomTypeId: number }[] = [];
                        props.data.forEach(room => {
                            if(room.roomType.id === roomTypeId.roomType.id) {
                                room.bookingDetails.forEach(bookingDetail => {
                                    const index = available.findIndex(x => DateUtil.compareOnAsIsoDate(x.date, bookingDetail.date));
                                    const availableItem = index > -1 ? available[index] : {
                                        date: bookingDetail.date,
                                        count: 0,
                                        roomTypeId: room.roomType.id
                                    };
                                    availableItem.count++;
                                    if (index > -1) {
                                        available[index] = availableItem;
                                    } else available.push(availableItem);
                                })
                            }
                        })
                        return (
                            <tr key={roomTypeId.roomType.id} className="border-t border-gray-200">
                                <td className="sticky-col">
                                    <RoomTypeView color={roomType.roomType.color} name={roomType.roomType.name[0]} />
                                </td>
                                {props.dates.map(date => {
                                    const index = available.findIndex(x => DateUtil.compareOnAsIsoDate(x.date, date))
                                    const item = {
                                        date: date,
                                        count: index > -1 ? (roomCount - available[index].count) : roomCount,
                                        roomTypeId: roomType.roomType.id
                                    };
                                    let className = 'text-success';
                                    if (item.count === 1) {
                                        className = 'text-warning';
                                    }
                                    if (item.count === 0) {
                                        className = 'text-danger';
                                    }

                                    let isSelectedDate = isSelected(props.selectedRoomTypeId, item.roomTypeId, date, props.selectedDates)
                                    return (
                                        <td key={DateUtil.toIsoDate(date) + "-room-type"} className={isSelectedDate}>
                                            <div className="w-full h-full flex justify-center items-center">
                                                <span className={className}>
                                                    {item.count}
                                                </span>
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        )
                    })
                }
            </tbody>
        </table>
    );
};


function isSelected(selectedRoomTypeId: number | undefined, roomTypeId: number, date: Date, selectedDates?: Date[]) {
    let isSelectedDate = '';
    if (selectedRoomTypeId === roomTypeId && selectedDates?.some(d => DateUtil.compareOnAsIsoDate(d, date))) {
        isSelectedDate = 'selected';
    } else if (selectedRoomTypeId && selectedRoomTypeId === roomTypeId && !selectedDates?.length) {
        isSelectedDate = 'selected';
    } else if (selectedDates?.some(d => DateUtil.compareOnAsIsoDate(d, date)) && !selectedRoomTypeId) {
        isSelectedDate = 'selected';
    }

    return isSelectedDate;
}

export default AvailabilityTable;
